import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowRight,
  Building2,
  ChevronRight,
  Clock3,
  ExternalLink,
  Info,
  Languages,
  Loader2,
  MapPin,
  ScanSearch,
  ShieldCheck,
  Sparkles,
  Ticket
} from 'lucide-react';

const DEFAULT_THEME = {
  backgroundStart: '#07111f',
  backgroundMid: '#0c1627',
  backgroundEnd: '#121c2f',
  accent: '#38bdf8',
  accentSoft: 'rgba(56, 189, 248, 0.14)',
  text: '#f8fafc',
  textMuted: '#cbd5e1'
};

const VENUE_CONFIG_URLS = [
  (museumId) => `/venue-pages/${encodeURIComponent(museumId)}.json`,
  (museumId) => `/museum-pages/${encodeURIComponent(museumId)}.json`,
  (museumId) => `https://storage.googleapis.com/ai-tourguide-1dc92.firebasestorage.app/special_itineraries/venue-pages/${encodeURIComponent(museumId)}.json`
];

const getVenueLabel = (entityType = 'venue') => {
  const normalized = String(entityType || 'venue').toLowerCase();
  if (normalized === 'museum') return 'Museum';
  if (normalized === 'monument') return 'Monument';
  if (normalized === 'business') return 'Business';
  if (normalized === 'site') return 'Site';
  if (normalized === 'place') return 'Place';
  return 'Venue';
};

const buildFallbackMuseumConfig = (museumId) => ({
  museumId,
  entityType: 'venue',
  displayName: 'Venue',
  welcomeTitle: 'Welcome',
  welcomeMessage: 'Choose a guide below to start your visit.',
  logoUrl: '',
  heroImageUrl: '',
  info: {
    label: 'Venue info',
    url: `/venue/${encodeURIComponent(museumId)}/info`
  },
  guides: [],
  theme: DEFAULT_THEME
});

const normalizeGuide = (guide, index) => ({
  id: guide.id || guide.label || `guide-${index}`,
  label: guide.label || guide.name || `Guide ${index + 1}`,
  description: guide.description || guide.subtitle || '',
  url: guide.url || guide.href || '#',
  imageUrl: guide.imageUrl || guide.thumbnail || '',
  badge: guide.badge || guide.audience || guide.language || '',
  featured: Boolean(guide.featured),
  tone: guide.tone || 'default'
});

const GUIDE_GRADIENTS = [
  'from-sky-400/25 via-cyan-300/10 to-transparent',
  'from-emerald-400/25 via-teal-300/10 to-transparent',
  'from-amber-400/25 via-orange-300/10 to-transparent',
  'from-fuchsia-400/25 via-pink-300/10 to-transparent',
  'from-violet-400/25 via-indigo-300/10 to-transparent'
];

const getGuideGradient = (index) => GUIDE_GRADIENTS[index % GUIDE_GRADIENTS.length];

const StatPill = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/6 px-4 py-3">
    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10">
      <Icon className="h-5 w-5 text-sky-100" />
    </div>
    <div>
      <p className="text-[11px] uppercase tracking-[0.24em] text-white/45">{label}</p>
      <p className="text-sm text-white/85">{value}</p>
    </div>
  </div>
);

const MuseumWelcomePage = ({ museumId }) => {
  const [config, setConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadConfig = async () => {
      setIsLoading(true);
      setError(null);

      try {
        let response = null;
        for (const buildUrl of VENUE_CONFIG_URLS) {
          // eslint-disable-next-line no-await-in-loop
          const candidate = await fetch(buildUrl(museumId), { cache: 'no-store' });
          if (candidate.ok) {
            response = candidate;
            break;
          }
          if (!response) response = candidate;
        }

        if (!response || !response.ok) {
          throw new Error(`Venue page not found (${response?.status || 'unknown'})`);
        }

        const data = await response.json();
        const entityType = data.entityType || data.type || 'venue';
        const label = getVenueLabel(entityType);

        if (!isMounted) return;

        setConfig({
          museumId: data.museumId || museumId,
          entityType,
          displayName: data.displayName || data.museumName || data.name || label,
          museumName: data.museumName || data.name || label,
          welcomeTitle: data.welcomeTitle || data.title || 'Welcome',
          welcomeMessage: data.welcomeMessage || data.description || 'Choose a guide below to start your visit.',
          logoUrl: data.logoUrl || data.logo || '',
          heroImageUrl: data.heroImageUrl || data.heroImage || '',
          info: data.info || {
            label: `${label} info`,
            url: `/venue/${encodeURIComponent(museumId)}/info`
          },
          guides: Array.isArray(data.guides) ? data.guides.map(normalizeGuide) : [],
          theme: { ...DEFAULT_THEME, ...(data.theme || {}) }
        });
      } catch (loadError) {
        if (!isMounted) return;
        setError(loadError.message || 'Failed to load venue page');
        setConfig(buildFallbackMuseumConfig(museumId));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadConfig();

    return () => {
      isMounted = false;
    };
  }, [museumId]);

  const theme = config?.theme || DEFAULT_THEME;
  const venueLabel = getVenueLabel(config?.entityType);

  const guideCards = useMemo(() => {
    const guides = config?.guides || [];
    return [...guides].sort((a, b) => {
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured) return 1;
      return a.label.localeCompare(b.label);
    });
  }, [config]);

  const primaryGuide = guideCards[0];
  const secondaryGuides = guideCards.slice(1);
  const guideCountText = `${guideCards.length} ${guideCards.length === 1 ? 'guide' : 'guides'}`;

  const backgroundStyle = {
    background: `radial-gradient(circle at 15% 20%, ${theme.accentSoft} 0%, rgba(7, 17, 31, 0) 28%), radial-gradient(circle at 85% 12%, rgba(255,255,255,0.08) 0%, rgba(7, 17, 31, 0) 30%), linear-gradient(180deg, ${theme.backgroundStart} 0%, ${theme.backgroundMid} 52%, ${theme.backgroundEnd} 100%)`,
    color: theme.text
  };

  return (
    <div className="relative min-h-screen overflow-hidden" style={backgroundStyle}>
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-28 top-20 h-80 w-80 rounded-full bg-sky-300/10 blur-3xl" />
        <div className="absolute right-[-7rem] top-36 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute bottom-[-7rem] left-1/2 h-96 w-[44rem] -translate-x-1/2 rounded-full bg-indigo-500/10 blur-3xl" />
      </div>

      <div className="relative mx-auto flex min-h-screen w-full max-w-7xl flex-col px-4 py-5 sm:px-6 lg:px-8 lg:py-8">
        <header className="mb-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-white/10 shadow-lg shadow-sky-950/20 backdrop-blur">
              <ScanSearch className="h-5 w-5 text-sky-200" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.35em] text-white/50">
                {venueLabel} welcome
              </p>
              <h1 className="text-lg font-semibold text-white sm:text-xl">
                {config?.displayName || config?.museumName || venueLabel}
              </h1>
            </div>
          </div>

          {config?.info?.url && (
            <a
              href={config.info.url}
              className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/10 px-4 py-2 text-sm font-medium text-white shadow-sm shadow-sky-950/10 transition hover:-translate-y-0.5 hover:bg-white/16"
            >
              <Info className="h-4 w-4" />
              {config.info.label || `${venueLabel} info`}
            </a>
          )}
        </header>

        <main className="flex flex-1 flex-col pb-8 pt-2">
          {isLoading && (
            <div className="flex flex-1 flex-col items-center justify-center gap-4 py-20 text-center">
              <Loader2 className="h-10 w-10 animate-spin text-sky-300" />
              <p className="text-sm text-white/70">Loading venue landing page...</p>
            </div>
          )}

          {!isLoading && (
            <>
              <section className="grid gap-6 xl:grid-cols-[1.12fr_0.88fr] xl:items-stretch">
                <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-white/6 p-6 shadow-2xl shadow-sky-950/30 backdrop-blur-sm sm:p-8">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.16),transparent_30%),radial-gradient(circle_at_bottom_left,rgba(56,189,248,0.14),transparent_28%)]" />

                  <div className="relative z-10 grid gap-8 lg:grid-cols-[1fr_280px] lg:items-center">
                    <div className="space-y-5">
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="inline-flex items-center gap-2 rounded-full border border-sky-300/20 bg-sky-300/10 px-4 py-2 text-xs font-medium uppercase tracking-[0.26em] text-sky-50">
                          <Sparkles className="h-4 w-4" />
                          Start here
                        </span>
                        <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/8 px-4 py-2 text-xs font-medium uppercase tracking-[0.26em] text-white/72">
                          <MapPin className="h-4 w-4" />
                          {guideCountText}
                        </span>
                        <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/8 px-4 py-2 text-xs font-medium uppercase tracking-[0.26em] text-white/72">
                          <Languages className="h-4 w-4" />
                          Languages, audiences, tours
                        </span>
                      </div>

                      <div className="space-y-4">
                        {config?.logoUrl ? (
                          <img
                            src={config.logoUrl}
                            alt={`${config.displayName || config.museumName || venueLabel} logo`}
                            className="h-20 w-auto object-contain drop-shadow-[0_8px_24px_rgba(15,23,42,0.35)]"
                          />
                        ) : (
                          <div className="flex h-16 w-16 items-center justify-center rounded-3xl border border-white/10 bg-white/10">
                            <Building2 className="h-7 w-7 text-sky-100" />
                          </div>
                        )}

                        <h2 className="max-w-2xl text-4xl font-semibold tracking-tight text-white sm:text-5xl">
                          {config?.welcomeTitle || 'Welcome'}
                        </h2>

                        <p className="max-w-2xl text-base leading-8 text-white/72 sm:text-lg">
                          {config?.welcomeMessage}
                        </p>
                      </div>

                      <div className="grid gap-3 sm:grid-cols-3">
                        <StatPill icon={ShieldCheck} label="Simple" value="One QR, many entry points" />
                        <StatPill icon={Clock3} label="Fast" value="Tap and start immediately" />
                        <StatPill icon={Ticket} label="Flexible" value="Adults, kids, or languages" />
                      </div>
                    </div>

                    <aside className="relative overflow-hidden rounded-[1.75rem] border border-white/10 bg-slate-950/30 p-4">
                      {config?.heroImageUrl ? (
                        <img
                          src={config.heroImageUrl}
                          alt=""
                          className="h-72 w-full rounded-[1.4rem] object-cover"
                        />
                      ) : (
                        <div className="flex h-72 items-center justify-center rounded-[1.4rem] border border-dashed border-white/10 bg-gradient-to-br from-white/10 via-white/5 to-transparent">
                          <div className="text-center">
                            <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-sky-300/10">
                              <ArrowRight className="h-7 w-7 text-sky-100" />
                            </div>
                            <p className="text-sm text-white/70">Pick the right guide for this visitor</p>
                          </div>
                        </div>
                      )}

                      <div className="mt-4 space-y-2">
                        <p className="text-xs uppercase tracking-[0.24em] text-white/45">Quick context</p>
                        <p className="text-sm leading-6 text-white/70">
                          Each option below can point to a different audience, language, or style of visit.
                        </p>
                      </div>
                    </aside>
                  </div>
                </div>

                <div className="grid gap-4">
                  <div className="rounded-[2rem] border border-white/10 bg-white/6 p-5 shadow-xl shadow-sky-950/20 backdrop-blur-sm">
                    <div className="flex items-start gap-4">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-sky-300/10">
                        <Info className="h-5 w-5 text-sky-100" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs uppercase tracking-[0.25em] text-white/45">
                          {venueLabel} information
                        </p>
                        <p className="mt-1 text-base font-medium text-white">
                          {config?.info?.label || `${venueLabel} info`}
                        </p>
                        <p className="mt-2 text-sm leading-6 text-white/68">
                          Practical details, opening hours, and visitor guidance live here.
                        </p>
                      </div>
                    </div>

                    {config?.info?.url && (
                      <a
                        href={config.info.url}
                        className="mt-4 inline-flex w-full items-center justify-between rounded-2xl border border-white/10 bg-white/8 px-4 py-3 text-sm font-medium text-white transition hover:bg-white/12"
                      >
                        Open information
                        <ArrowRight className="h-4 w-4" />
                      </a>
                    )}
                  </div>

                  <div className="rounded-[2rem] border border-white/10 bg-white/5 p-5">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs uppercase tracking-[0.25em] text-white/45">Suggested flow</p>
                        <h3 className="mt-1 text-xl font-semibold text-white">Choose the right entrance</h3>
                      </div>
                      <span className="rounded-full border border-white/10 bg-white/8 px-3 py-1 text-xs text-white/70">
                        {guideCountText}
                      </span>
                    </div>

                    <div className="mt-4 space-y-3 text-sm leading-6 text-white/68">
                      <p>Use the top guide for the most common visitor profile.</p>
                      <p>Use the info page for opening hours, tickets, and practical details.</p>
                    </div>
                  </div>
                </div>
              </section>

              <section className="mt-10">
                <div className="mb-5 flex items-end justify-between gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-white/45">Available guides</p>
                    <h3 className="mt-1 text-2xl font-semibold text-white">Choose a version</h3>
                  </div>
                  <div className="hidden text-sm text-white/55 sm:block">
                    {guideCards.length} option{guideCards.length === 1 ? '' : 's'}
                  </div>
                </div>

                {guideCards.length === 0 ? (
                  <div className="rounded-[2rem] border border-white/10 bg-white/5 p-8 text-center text-white/70">
                    No guide options are configured yet.
                  </div>
                ) : (
                  <div className="grid gap-4 lg:grid-cols-[1.12fr_0.88fr]">
                    {primaryGuide && (
                      <a
                        href={primaryGuide.url}
                        className="group relative overflow-hidden rounded-[2rem] border border-sky-300/20 bg-white/8 p-6 shadow-2xl shadow-sky-950/20 backdrop-blur-sm transition hover:-translate-y-0.5 hover:border-sky-300/35 hover:bg-white/12"
                      >
                        <div className={`absolute inset-0 bg-gradient-to-br ${getGuideGradient(0)} opacity-90`} />
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.18),transparent_28%)]" />
                        <div className="relative z-10 flex h-full flex-col">
                          {primaryGuide.imageUrl ? (
                            <img
                              src={primaryGuide.imageUrl}
                              alt=""
                              className="mb-5 h-48 w-full rounded-[1.5rem] object-cover shadow-lg shadow-slate-950/20"
                            />
                          ) : (
                            <div className="mb-5 flex h-48 w-full items-center justify-center rounded-[1.5rem] border border-white/10 bg-slate-950/20">
                              <div className="text-center">
                                <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10">
                                  <ArrowRight className="h-6 w-6 text-white" />
                                </div>
                                <p className="text-sm text-white/80">Open the main visitor guide</p>
                              </div>
                            </div>
                          )}

                          <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0">
                              <div className="mb-3 flex flex-wrap items-center gap-2">
                                <span className="rounded-full border border-white/15 bg-white/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.25em] text-white">
                                  Featured guide
                                </span>
                                {primaryGuide.badge && (
                                  <span className="rounded-full border border-white/15 bg-white/12 px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-white/85">
                                    {primaryGuide.badge}
                                  </span>
                                )}
                              </div>
                              <h3 className="text-2xl font-semibold text-white">{primaryGuide.label}</h3>
                              {primaryGuide.description && (
                                <p className="mt-3 max-w-xl text-sm leading-6 text-white/82">
                                  {primaryGuide.description}
                                </p>
                              )}
                            </div>

                            <div className="mt-1 flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/15 transition group-hover:bg-white/20">
                              <ChevronRight className="h-5 w-5 text-white transition group-hover:translate-x-0.5" />
                            </div>
                          </div>

                          <div className="mt-6 flex items-center justify-between text-sm text-white/80">
                            <span>Open this first</span>
                            <ExternalLink className="h-4 w-4" />
                          </div>
                        </div>
                      </a>
                    )}

                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-1">
                      {secondaryGuides.length === 0 ? (
                        <div className="rounded-[2rem] border border-white/10 bg-white/5 p-6 text-white/70">
                          Additional guide options can be added here later.
                        </div>
                      ) : (
                        secondaryGuides.map((guide, index) => (
                          <a
                            key={guide.id}
                            href={guide.url}
                            className="group relative overflow-hidden rounded-[2rem] border border-white/10 bg-white/7 p-5 backdrop-blur-sm transition hover:-translate-y-0.5 hover:border-sky-300/30 hover:bg-white/10"
                          >
                            <div className={`absolute inset-0 bg-gradient-to-br ${getGuideGradient(index + 1)} opacity-60`} />
                            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.14),transparent_30%)]" />
                            <div className="relative z-10">
                              {guide.imageUrl && (
                                <img
                                  src={guide.imageUrl}
                                  alt=""
                                  className="mb-4 h-32 w-full rounded-2xl object-cover"
                                />
                              )}

                              <div className="flex items-start justify-between gap-4">
                                <div>
                                  <div className="mb-2 flex flex-wrap items-center gap-2">
                                    <span className="text-base font-semibold text-white">{guide.label}</span>
                                    {guide.badge && (
                                      <span className="rounded-full border border-white/12 bg-white/12 px-2.5 py-1 text-[11px] font-medium uppercase tracking-wide text-white/82">
                                        {guide.badge}
                                      </span>
                                    )}
                                  </div>
                                  {guide.description && (
                                    <p className="text-sm leading-6 text-white/78">{guide.description}</p>
                                  )}
                                </div>

                                <div className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/12 transition group-hover:bg-white/18">
                                  <ChevronRight className="h-5 w-5 text-white transition group-hover:translate-x-0.5" />
                                </div>
                              </div>

                              <div className="mt-5 flex items-center justify-between text-sm text-white/70">
                                <span>Open guide</span>
                                <ExternalLink className="h-4 w-4" />
                              </div>
                            </div>
                          </a>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </section>

              {config?.info?.url && (
                <section className="mt-8">
                  <a
                    href={config.info.url}
                    className="group flex items-center justify-between gap-4 rounded-[2rem] border border-white/10 bg-white/6 px-5 py-4 text-white shadow-lg shadow-sky-950/10 transition hover:-translate-y-0.5 hover:bg-white/10"
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10">
                        <Info className="h-5 w-5 text-sky-200" />
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-[0.25em] text-white/45">
                          {venueLabel} information
                        </p>
                        <p className="text-base font-medium">{config.info.label || `${venueLabel} info`}</p>
                      </div>
                    </div>
                    <ArrowRight className="h-5 w-5 text-white/70 transition group-hover:translate-x-0.5" />
                  </a>
                </section>
              )}
            </>
          )}

          {error && (
            <div className="mt-8 rounded-[2rem] border border-amber-400/20 bg-amber-400/10 p-4 text-sm text-amber-50">
              <p className="font-medium">Config load issue</p>
              <p className="mt-1 text-amber-100/80">{error}</p>
              <p className="mt-2 text-amber-100/70">
                Expected a config file at <code>/venue-pages/{museumId}.json</code> or <code>/museum-pages/{museumId}.json</code>
              </p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default MuseumWelcomePage;
